import React, { useCallback } from "react";

import {
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { Constants } from "Components/AppGrid/shipeezi-new-grid";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";

import History, { Item } from "../../../../../../../components/Generic/History";
import SideViewComponent, {
  Navigation,
  PropHandles,
} from "../../../../../../../components/Generic/ViewJobComponent";
import { ItemJob } from "../../../../../../../models/sales";
import { StorageBox } from "../../../../../../../models/storage.box";
import { ItemDetail } from "../ComponentDetail/ItemDetail";
import { StorageBoxDetail } from "../ComponentDetail/StorageBoxDetail";
import { OrdersContainer, TableStyle } from "./style";

interface Props {
  sideViewRef: React.Ref<PropHandles>;
  loading: boolean;
  itemJob?: ItemJob;
  storageBox?: StorageBox;
  title: string;
}

export const MoreDetails = ({ sideViewRef, loading, itemJob, storageBox, title }: Props) => {
  const { t: translate } = useTranslation();

  const callbacksHandle = [itemJob, storageBox];

  const getItemsToHistory = useCallback(() => {
    const itemHistories = itemJob?.itemHistories;

    if (itemHistories && itemHistories?.length) {
      return itemHistories
        ?.map((history) => {
          let date;

          if (history?.createdAt) {
            date = format(new Date(history?.createdAt), "dd/MM/yyyy p");
          }

          return {
            title: history?.comment || history.eventType?.description,
            activityDate: date ?? "",
            activityUser: `${history?.user?.name} ${history?.user?.lastName}`,
          } as Item;
        })
        ?.reverse();
    }

    const storageBoxHistories = storageBox?.storageBoxHistories;

    if (storageBoxHistories && storageBoxHistories?.length) {
      return storageBoxHistories
        ?.map((history) => {
          let date;

          if (history?.createdAt) {
            date = format(new Date(history?.createdAt), "dd/MM/yyyy p");
          }

          return {
            title: history?.description,
            activityDate: date ?? "",
            activityUser: `${history?.user?.name} ${history?.user?.lastName}`,
          } as Item;
        })
        ?.reverse();
    }
  }, callbacksHandle);

  const handleItemToRender = useCallback(() => {
    let component: JSX.Element = <></>;

    if (itemJob) component = <ItemDetail itemJob={itemJob} />;
    if (storageBox) component = <StorageBoxDetail storageBox={storageBox} isFullDetails={true} />;

    return <OrdersContainer>{component}</OrdersContainer>;
  }, callbacksHandle);

  const Row = (props: {
    job: { reference: string; customer: string; itemQuantity: number; items: any[] };
  }) => {
    const { job } = props;
    const [open, setOpen] = React.useState(false);

    return (
      <React.Fragment>
        <TableRow>
          <TableCell component="th" scope="row">
            {job.reference}
          </TableCell>
          <TableCell>{job.customer}</TableCell>
          <TableCell align="right">{job.itemQuantity}</TableCell>
          <TableCell>
            <IconButton aria-label="expand row" size="small" onClick={() => setOpen(!open)}>
              {open ? (
                <IoIosArrowUp color="#667085" size={Constants.defaultRowActionSize} />
              ) : (
                <IoIosArrowDown color="#667085" size={Constants.defaultRowActionSize} />
              )}
            </IconButton>
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell padding="none" colSpan={6}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Table aria-label="purchases">
                <TableHead>
                  <TableRow>
                    <TableCell>{translate("item")} #</TableCell>
                    <TableCell>{translate("description")}</TableCell>
                    <TableCell align="right">{translate("quantity")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {job.items.map((item, idx) => (
                    <TableRow key={idx + item.description}>
                      <TableCell component="th" scope="row">
                        {idx + 1}
                      </TableCell>
                      <TableCell>{item.description}</TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Collapse>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  const handleOrdersToRender = useCallback(() => {
    if (!storageBox || !storageBox.itemsJobs) return <></>;

    const { itemsJobs } = storageBox;
    const jobsMap = itemsJobs.reduce((jobs, item: any) => {
      if (!item.job!.id) return jobs;

      const { job, StorageBoxToItemJob, description, volume, weight } = item;
      const quantity = StorageBoxToItemJob.itemQuantity;

      const currentJob = jobs.get(job!.id) || {
        reference: job.reference,
        customer: job.customer.name,
        itemQuantity: 0,
        items: [],
      };

      currentJob.itemQuantity += quantity;
      currentJob.items = [...currentJob.items, { description, volume, weight, quantity }];
      jobs.set(job.id, currentJob);

      return jobs;
    }, new Map<number, any>());

    const jobs = Array.from(jobsMap.values());

    return (
      <TableStyle>
        <Table aria-label="collapsible table">
          <TableHead>
            <TableRow>
              <TableCell>
                <div>{translate("order")} #</div>
              </TableCell>
              <TableCell>{translate("customer")}</TableCell>
              <TableCell align="right">{translate("itemsInsideThisStorageBox")}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.map((job) => (
              <Row key={job.reference} job={job} />
            ))}
          </TableBody>
        </Table>
      </TableStyle>
    );
  }, callbacksHandle);

  const handleSetTabs = useCallback(() => {
    const histories = getItemsToHistory();

    const tabs = [
      {
        key: Navigation.DETAILS,
        loading,
        isEmpty: !itemJob && !storageBox,
        render: () => handleItemToRender(),
      },
    ];

    if (storageBox) {
      tabs.push({
        key: Navigation.ORDERS,
        loading,
        isEmpty: !storageBox,
        render: () => handleOrdersToRender(),
      });
    }

    tabs.push({
      key: Navigation.ACTIVITY,
      loading,
      isEmpty: !itemJob && !storageBox,
      render: () => <History items={histories} />,
    });

    return tabs;
  }, callbacksHandle);

  const handleSetNavigation = useCallback(() => {
    const navList = [{ key: Navigation.DETAILS }];

    if (storageBox) {
      navList.push({ key: Navigation.ORDERS });
    }

    navList.push({ key: Navigation.ACTIVITY });

    return navList;
  }, callbacksHandle);

  return (
    <SideViewComponent
      ref={sideViewRef}
      isLoading={loading}
      title={translate(title)}
      tabs={handleSetTabs()}
      navigation={handleSetNavigation()}
      data-cy="side-view-component-details"
    />
  );
};
